import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MsalProvider, MsalAuthenticationTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react'
import {
  EventType,
  InteractionStatus,
  InteractionType,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser'
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge'

// Brand fonts — latin subset only, bundled (base64-inlined) for a self-contained file.
import '@fontsource/source-serif-4/latin-300.css'
import '@fontsource/source-serif-4/latin-400.css'
import '@fontsource/source-serif-4/latin-600.css'
import '@fontsource/source-serif-4/latin-700.css'
import '@fontsource/source-serif-4/latin-300-italic.css'
import '@fontsource/source-serif-4/latin-400-italic.css'
import '@fontsource/nunito-sans/latin-300.css'
import '@fontsource/nunito-sans/latin-400.css'
import '@fontsource/nunito-sans/latin-600.css'
import '@fontsource/nunito-sans/latin-700.css'

import './styles/tokens.css'
import './styles/base.css'
import './styles/shell.css'
import './components/ui/ui.css'
import './components/charts/charts.css'
import './components/workspace/workspace.css'
import './components/finance/finance.css'
import './components/timeline/timeline.css'

import App from './App'
import { msalInstance, loginRequest } from './auth/msalConfig'
import { DataGate } from './components/shell/DataGate'
import {
  arrivalLoginHint,
  claimAutoSignIn,
  linkAccount,
  rememberSignedIn,
  shouldSignInAutomatically,
  signedInAccount,
  signedOutOnPurpose,
} from './auth/ssoMemory'
import { linkLoginHint } from './auth/linkHint'

// Minimal pre-auth screen (shown while the redirect round-trip completes /
// if sign-in fails). Deliberately plain — @wise/ui reconciliation of the
// portal's look is a later prompt (see CLAUDE.md).
function PreAuthScreen({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
      }}
    >
      <strong>WPI CRM</strong>
      <div>{children}</div>
    </div>
  )
}

// --- MSAL v5 silent-renewal redirect bridge (CRITICAL) -------------------
//
// In MSAL browser v5 the silent token flow (acquireTokenSilent's hidden-iframe
// fallback) no longer polls the iframe URL. Instead the parent frame waits on
// a BroadcastChannel and the page that loads at the redirect URI *inside the
// iframe* must hand the auth response back by calling
// broadcastResponseToMainFrame(). MSAL never calls this for us. If the iframe
// instead boots the full SPA (which never broadcasts), the parent waits out
// `system.iframeBridgeTimeout` and throws `timed_out` — every single time.
// That is the bug that made the wise-iam portal unusable (2026-05): bumping
// the timeout could never help because the message was never coming.
//
// Fix: when this document is itself an auth response loaded inside an iframe
// (i.e. the hidden silent-renewal frame), run the redirect bridge and DO NOT
// boot the SPA. The top-level interactive redirect (window === top) is left to
// the normal initialize()/MsalProvider path below — that flow already works.
// An auth response can arrive in the fragment (default fragment mode) or the
// query (form_post / EAR), carrying code, error, or ear_jwe.
const isAuthResponse = /[?#&](code|error|ear_jwe)=/.test(
  window.location.hash + window.location.search,
)
const inHiddenFrame = window.self !== window.top

// Investor Portal (spec §7): external invitees, verified-email sign-in —
// completely outside MSAL and the CRM shell.
if (window.location.pathname.startsWith('/symposium')) {
  // Two anonymous pages under one prefix, split HERE rather than in a router:
  // everything below /symposium is outside MSAL, and App.tsx's BrowserRouter
  // never boots on these paths.
  //
  //   /symposium/rsvp      public self-registration — anyone with the link
  //                        (also /register; 2026-09-16)
  //   /symposium           the invitee portal: sign in, then RSVP
  //
  // The App Service already serves both deep links (SPA fallback), so this
  // needed no route, host or DNS change.
  const isRegister = /^\/symposium\/(rsvp|register)\/?$/.test(window.location.pathname)
  const page = isRegister
    ? import('./pages/investor/InvestorRegister')
    : import('./pages/investor/InvestorPortal')
  void page.then(({ default: Page }) => {
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode><Page /></React.StrictMode>,
    )
  })
} else if (isAuthResponse && inHiddenFrame) {
  // Posts the response to the parent over BroadcastChannel, then closes the
  // frame. Resolves the parent's acquireTokenSilent. Never renders React.
  void broadcastResponseToMainFrame().catch((err) => {
    // eslint-disable-next-line no-console
    console.error('[rems-crm] redirect bridge failed to broadcast auth response', err)
  })
} else {
// EVERY LINK LANDS SIGNED IN (the platform rule, Chauncey White 2026-10-23:
// "EVERY link emailed or sent or coded should check to see if the user is
// logged in"; rolled 2026-11-02 — auth/ssoMemory.ts). The automatic sign-in
// is MsalAuthenticationTemplate's own redirect, below — there is never a
// second one beside it. What it is handed: the person the LINK names
// (?login_hint, in the query or inside the hash — the Launchpad's handshake or
// an emailed link the shared sender stamped), else whoever last signed in on
// this browser. Entra resolves that account from its session cookie and comes
// back without a prompt; without a hint, a browser holding more than one
// signed-in account stops on an account picker.
const authenticationRequest = (() => {
  const hint = arrivalLoginHint(linkLoginHint(window.location.search, window.location.hash))
  return hint ? { ...loginRequest, loginHint: hint } : loginRequest
})()

// The sign-in page a person sees only when the automatic sign-in is NOT
// theirs to get: after a chosen Sign out, after this tab's one try came back
// without a session, or when the redirect could not start. Plain, like the
// pre-auth screen. A click names the link's person if the link names one.
function SignInButton() {
  const { instance } = useMsal()
  const hint = linkLoginHint(window.location.search, window.location.hash)
  return (
    <button
      type="button"
      onClick={() => void instance.loginRedirect({ ...loginRequest, ...(hint ? { loginHint: hint } : {}) })}
    >
      Sign in with Microsoft
    </button>
  )
}

// Whether THIS page load runs the template's automatic redirect. Decided ONCE,
// outside React (StrictMode re-runs effects and initialisers; a per-tab claim
// must not be spent twice): only when nobody is cached, never on the page
// carrying Entra's answer, never after a chosen Sign out, once per tab (the
// sessionStorage guard, cleared by a successful sign-in) — so a round trip
// that comes back without a session gets the page instead of a loop.
function decideAutoSignIn(): boolean {
  if (msalInstance.getAllAccounts().length > 0 || isAuthResponse) return false
  return shouldSignInAutomatically() && claimAutoSignIn()
}

function SignInGate({ autoSignIn, children }: { autoSignIn: boolean; children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated()
  const { inProgress } = useMsal()
  // Signed in: the CRM itself, never the template. Now that the link's person
  // is made ACTIVE (below), a template rendered for someone signed in runs its
  // own acquireTokenSilent on every load, and any failure that is not
  // "interaction required" (a network blip, a renewal-frame timeout) would
  // replace the whole CRM with "Sign-in failed". Before 2026-11-02 no account
  // was ever active, so that call never ran: the template is only the
  // automatic sign-in for a tab with nobody in it.
  if (isAuthenticated) return <>{children}</>
  if (autoSignIn) {
    return (
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authenticationRequest}
        loadingComponent={() => <PreAuthScreen>Signing you in…</PreAuthScreen>}
        errorComponent={({ error }) => (
          <PreAuthScreen>
            <span style={{ color: '#b00020', fontWeight: 700 }}>Sign-in failed</span>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, textAlign: 'left' }}>
              {error?.errorMessage ?? String(error)}
            </pre>
            {/* The redirect could not start (or Entra refused): the page,
                never a dead end. */}
            <SignInButton />
          </PreAuthScreen>
        )}
      >
        {children}
      </MsalAuthenticationTemplate>
    )
  }
  if (inProgress !== InteractionStatus.None) return <PreAuthScreen>Signing you in…</PreAuthScreen>
  return (
    <PreAuthScreen>
      <SignInButton />
    </PreAuthScreen>
  )
}

// MSAL MUST be initialised before any React hook touches its state. Without
// this, useMsal() returns inProgress='startup' indefinitely and pages spin
// forever. Same gotcha wise-shell hit; same fix.
void msalInstance.initialize().then(() => {
  // Remember who signed in: the hint for the next link that carries none.
  // Whether they signed out ON PURPOSE is the menu's to say (useSignOut),
  // never LOGOUT_START.
  // ⚠ msal-browser 5.x payloads differ by event (checked in the installed
  // 5.17.0's StandardController): LOGIN_SUCCESS carries the AccountInfo
  // ITSELF; ACQUIRE_TOKEN_SUCCESS carries the AuthenticationResult.
  // `.account` on LOGIN_SUCCESS is always undefined.
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      const account = (event.payload as AccountInfo | null) ?? null
      rememberSignedIn(account?.username)
      // Whoever just signed in is who this tab is — not the first cached.
      if (account !== null) msalInstance.setActiveAccount(account)
    } else if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
      rememberSignedIn((event.payload as AuthenticationResult | null)?.account?.username)
    }
  })

  // The cache is shared across tabs now: a tab opened from a link uses the
  // account already there — the one the link NAMES (auth/linkHint.ts), never
  // simply whoever is cached (on a shared PC, the last person who did not
  // sign out). A link for somebody the browser does not hold signs them in
  // instead — once per tab, never after a chosen sign-out, never on the page
  // carrying Entra's answer. (Nobody cached is the template's redirect, not
  // this one: the two never both run.)
  const linked = linkAccount(msalInstance.getAllAccounts(), linkLoginHint(window.location.search, window.location.hash))
  if (linked.account !== null) msalInstance.setActiveAccount(linked.account)
  else if (!msalInstance.getActiveAccount()) {
    const first = signedInAccount(msalInstance, msalInstance.getAllAccounts())
    if (first) msalInstance.setActiveAccount(first)
  }

  const autoSignIn = decideAutoSignIn()
  const render = (): void =>
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <SignInGate autoSignIn={autoSignIn}>
            {/* BrowserRouter (not the prototype's HashRouter) — per Jason's own
                deploy notes: when served with an SPA navigation fallback,
                switch to BrowserRouter so deep links work. portal/server.js
                provides the fallback on App Service. Future flags carried over
                from the prototype's HashRouter setup. */}
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              {/* DataGate hydrates the store from GET /api/crm/dataset before
                  the app renders — the bundled seed is gone (server-side now). */}
              <DataGate>
                <App />
              </DataGate>
            </BrowserRouter>
          </SignInGate>
        </MsalProvider>
      </React.StrictMode>,
    )

  if (linked.signInAs !== null && !isAuthResponse && !signedOutOnPurpose() && claimAutoSignIn()) {
    void msalInstance.loginRedirect({ ...loginRequest, loginHint: linked.signInAs }).catch((err: unknown) => {
      // The redirect itself failed (not Entra refusing): open as whoever is
      // cached rather than a blank page.
      console.warn("[rems-crm] sign-in for the link's person failed:", err)
      render()
    })
    return
  }
  render()
})
}
